import React, { useEffect, useMemo, useState } from "react";
import { Users, Search, Mail, ChevronLeft, ChevronRight } from "lucide-react";
import { ApiService } from "../../../services/api";
import DepartmentSelector from "../../../components/selectors/DepartmentSelector";

type PlanningStudent = {
  id: string;
  name?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  schoolNumber?: string;
  department?: { id: string; name: string } | null;
  parentDepartment?: { id: string; name: string } | null;
  isVerified?: boolean;
};

const StudentsList = ({ hidePageHeading = false }: { hidePageHeading?: boolean }) => {
  const [parentDepartmentId, setParentDepartmentId] = useState<string>("");
  const [search, setSearch] = useState<string>("");
  const [page, setPage] = useState<number>(1);
  const [limit] = useState<number>(20);
  const [sortBy] = useState<string>("firstName");
  const [sortOrder] = useState<string>("ASC");
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [settingsLoading, setSettingsLoading] = useState<boolean>(true);
  const [data, setData] = useState<{
    results: PlanningStudent[];
    page: number;
    limit: number;
    totalPages: number;
    totalResults: number;
  }>({ results: [], page: 1, limit: limit, totalPages: 0, totalResults: 0 });

  const displayName = (s: PlanningStudent) => s.name || `${s.firstName || ""} ${s.lastName || ""}`.trim();

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await ApiService.getSettings();
        const settings = res?.data ?? res;
        if (settings?.studentDepartmentId) {
          setParentDepartmentId(settings.studentDepartmentId);
        }
      } catch (e) {
        console.error("Settings alınırken hata:", e);
      } finally {
        setSettingsLoading(false);
      }
    };
    fetchSettings();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      if (!parentDepartmentId) return;
      try {
        setLoading(true);
        setError("");
        const res = await ApiService.getPlanningStudents({
          parentDepartmentId,
          page,
          limit,
          search: search.trim() || undefined,
          sortBy,
          sortOrder,
        });
        const normalized = (res?.data ?? res) as any;
        setData({
          results: normalized?.results || [],
          page: Number(normalized?.page || 1),
          limit: Number(normalized?.limit || limit),
          totalPages: Number(normalized?.totalPages || 0),
          totalResults: Number(normalized?.totalResults || 0),
        });
      } catch (e: any) {
        setError(e?.message || "Veri alınırken hata oluştu");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [parentDepartmentId, page, limit, search, sortBy, sortOrder]);

  const canLoad = useMemo(() => !!parentDepartmentId, [parentDepartmentId]);

  const paginationBar = (
    <div className="inline-flex max-w-full min-w-0 flex-wrap items-center gap-x-2 gap-y-1 rounded-lg border border-gray-200 bg-gray-50 px-2 py-1.5 text-xs text-gray-600 sm:px-2.5 sm:text-sm">
      <span>
        {data.totalResults > 0
          ? `${(page - 1) * limit + 1}-${Math.min(page * limit, data.totalResults)} / ${data.totalResults}`
          : "0 / 0"}
      </span>
      <button
        type="button"
        onClick={() => setPage((p) => Math.max(1, p - 1))}
        disabled={page <= 1}
        className="rounded p-1 text-gray-500 transition hover:bg-white hover:text-gray-700 disabled:cursor-not-allowed disabled:opacity-50">
        <ChevronLeft className="h-4 w-4" />
      </button>
      <button
        type="button"
        onClick={() => setPage((p) => p + 1)}
        disabled={page >= data.totalPages}
        className="rounded p-1 text-gray-500 transition hover:bg-white hover:text-gray-700 disabled:cursor-not-allowed disabled:opacity-50">
        <ChevronRight className="h-4 w-4" />
      </button>
    </div>
  );

  return (
    <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
      {!hidePageHeading && (
        <div className="border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white px-4 py-4 sm:px-6">
          <div className="min-w-0">
            <h2 className="text-lg font-bold text-gray-900 sm:text-xl">Öğrenciler</h2>
            <p className="mt-0.5 text-sm text-gray-500">Öğrenci departmanına göre planlama listesi</p>
          </div>
        </div>
      )}

      <div className="border-b border-gray-200 px-4 py-3 sm:px-6">
        <div className="flex flex-col gap-3 lg:flex-row lg:flex-wrap lg:items-end">
          <div className="min-w-0 flex-1 lg:max-w-md">
            <DepartmentSelector
              value={parentDepartmentId}
              onChange={(id) => {
                setParentDepartmentId(id);
                setPage(1);
              }}
              placeholder="Üst departman (Öğrenciler) seçin"
            />
          </div>
          <div className="relative min-h-[40px] min-w-0 flex-1 lg:max-w-xs">
            <Search size={18} className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              placeholder="Öğrenci ara..."
              className="h-10 w-full rounded-lg border border-gray-300 bg-white pl-9 pr-3 text-sm text-gray-700 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
            />
          </div>
          <div className="flex justify-end lg:ml-auto">{paginationBar}</div>
        </div>
      </div>

      <div className="p-4 sm:p-6">
        {settingsLoading ? (
          <div className="py-12 text-center text-sm text-gray-500">Ayarlar yükleniyor...</div>
        ) : !canLoad ? (
          <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50/80 py-12 text-center text-sm text-gray-600">
            Lütfen üst departman seçin.
          </div>
        ) : loading ? (
          <div className="py-12 text-center text-sm text-gray-500">Yükleniyor...</div>
        ) : error ? (
          <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-center text-sm text-red-600">{error}</div>
        ) : (
          <div className="overflow-x-auto rounded-xl border border-gray-200">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 sm:px-6">
                    Öğrenci
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 sm:px-6">
                    Numara
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 sm:px-6">
                    E-posta
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 sm:px-6">
                    Sınıf
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {data.results.map((s) => (
                  <tr key={s.id} className="transition-colors hover:bg-gray-50/80">
                    <td className="whitespace-nowrap px-4 py-3 sm:px-6">
                      <div className="flex items-center gap-3">
                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-violet-100">
                          <Users size={18} className="text-violet-600" />
                        </div>
                        <div className="text-sm font-medium text-gray-900">{displayName(s) || "-"}</div>
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-500 sm:px-6">{s.schoolNumber || "-"}</td>
                    <td className="whitespace-nowrap px-4 py-3 sm:px-6">
                      <div className="flex items-center text-sm text-gray-600">
                        <Mail size={14} className="mr-2 shrink-0 opacity-70" />
                        <span>{s.email || "-"}</span>
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-600 sm:px-6">{s.department?.name || "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default StudentsList;
